import {Injectable} from '@angular/core';

import {STORAGE_KEY} from '@app/core/config/app.config';
import {Range} from '@app/core/models/range.model';
import {Session} from '@app/core/models/session.model';
import {RangeSliderService} from '@app/core/services/range-slider/range-slider.service';
import {SessionService} from '@app/core/services/session.service';
import { DATE_FNS_ISO_DATE } from '@alpha-coach-bot/shared-util';
import {Week} from 'app/core/models/week.model';

import {addWeeks, format, isAfter, isBefore, isSameDay} from 'date-fns';
import {ObjectMapper} from 'json-object-mapper';
import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {distinctUntilChanged, filter, map, shareReplay, tap} from 'rxjs/operators';

export const CUSTOM_SESSION_ID   = 'custom';
export const CUSTOM_SESSION_NAME = 'Custom Date Range';

@Injectable({providedIn: 'root'})
export default class SessionState {
  /** Exposes the current session as observable */
  public currentSession$: Observable<Session>;
  /** Exposes the custom session as observable */
  public custom$: Observable<Session>;
  /**
   * Represent the number of weeks in a session.
   * Number of weeks can vary for each session.
   * Generating the number of weeks using session start and end date
   */
  public isoWeeks$: Observable<Week[]>;
  /** Exposes the filtered session as observable */
  public sessionFiltered$: Observable<Session>;
  /** Exposes the filtered session ID as observable */
  public sessionFilteredId$: Observable<string>;
  /** Exposes the ID of session with custom range as observable */
  public sessionWithCustomRangeId$: Observable<string>;
  /** Exposes the sessions as observable */
  public sessions$: Observable<Session[]>;
  /**
   * Observable to expose filtered sessions.
   * @type {Observable<Session[]>}
   */
  public sessionsFiltered$: Observable<Session[]>;
  /**
   * Observable to emit custom session range
   * @type {Observable<Session[]>}
   */
  public sessionsWithCustomRange$: Observable<Session[]>;
  /**
   * Represents the selected week number for week dropdown in daily view.
   * Get the selected week, if week is not selected then select the current active week as default week
   */
  public weekName$: Observable<string>;
  /**
   * Represent the number of weeks in a session.
   * Number of weeks can vary for each session.
   * Generating the number of weeks using session start and end date
   */
  public weeks$: Observable<Week[]>;
  /** Represents the state of custom session */
  private readonly _custom: BehaviorSubject<Session>;
  /** Represents the state of filtered session ID */
  private readonly _sessionFilteredId: BehaviorSubject<string>;
  /** Represents the state of ID for session with custom range */
  private readonly _sessionWithCustomRangeId: BehaviorSubject<string>;
  /** Represents the state sessions */
  private readonly _sessions: BehaviorSubject<Session[]>;
  /** Represents the state of currently active week */
  private readonly _weekName$: BehaviorSubject<string>;

  public constructor(private readonly _slider: RangeSliderService, private readonly _service: SessionService) {
    this._custom = this._initCustom();
    this.custom$ = this._custom.asObservable().pipe(filter<Session>(Boolean), distinctUntilChanged());

    this._sessions         = new BehaviorSubject([]);
    this.sessions$         = this._initSessions();
    this.currentSession$   = this.sessions$.pipe(map(sessions => sessions.find(session => session.current)));
    this.sessionsFiltered$ = this._initSessionsFiltered();

    this._sessionFilteredId = new BehaviorSubject(localStorage.getItem(STORAGE_KEY.sessionFilteredId));
    this.sessionFilteredId$ = this._initSessionFilteredId();

    this.sessionFiltered$ = this._initSessionFiltered();

    this._sessionWithCustomRangeId = this._initSessionWithCustomRangeId();
    this.sessionWithCustomRangeId$ = this._sessionWithCustomRangeId
      .asObservable()
      .pipe(filter<string>(Boolean), distinctUntilChanged());

    this.sessionsWithCustomRange$ = this._initSessionsWithCustomRange();

    /**
     * This exposes and updates the observables and Subjects
     * to control the weeks state
     */
    this._weekName$ = new BehaviorSubject('');
    this.weeks$            = this.sessionFiltered$.pipe(map(session => session.weeks));
    this.isoWeeks$         = this.sessionFiltered$.pipe(map(session => session.isoWeeks));
    const tempSubscription = this.weeks$.subscribe(weeks => {
      this._setWeekOnWeeksUpdate.call(this, weeks);
      tempSubscription.unsubscribe();
    });
    this.weekName$         = this._weekName$.asObservable();
  }

  /** To be used to set the custom state. */
  public updateCustom(range: Range): void {
    const custom = this._rangeToCustom(range);
    localStorage.setItem(STORAGE_KEY.sessionCustom, ObjectMapper.serialize(custom) as string);
    this._custom.next(custom);
  }

  /** To be used to set the selected session id. */
  public updateSessionFilteredId(value: string): void {
    localStorage.setItem(STORAGE_KEY.sessionFilteredId, value);
    this._sessionFilteredId.next(value);
  }

  /** To be used to set the selected session. */
  public updateSessionWithCustomRangeId(value: string): void {
    localStorage.setItem(STORAGE_KEY.sessionWithCustomRangeId, value);
    this._sessionWithCustomRangeId.next(value);
  }

  /** To be used to set the selected session. */
  public updateSessions(values: Session[]): void {
    this._sessions.next([...values]);
  }

  /** To be used to set the selected week, on changing the week dropdown value used in daily view. */
  public updateWeekName(weekName: string): void {
    this._weekName$.next(weekName);
  }

  /** Initialize custom observable */
  private _initCustom(): BehaviorSubject<Session> {
    const sessionCustom = JSON.parse(localStorage.getItem(STORAGE_KEY.sessionCustom));

    return new BehaviorSubject(
      sessionCustom
        ? ObjectMapper.deserialize(Session, sessionCustom)
        : this._rangeToCustom(JSON.parse(localStorage.getItem(STORAGE_KEY.range)))
    );
  }

  /** Initialize filtered session observable */
  private readonly _initSessionFiltered = (): Observable<Session> => combineLatest([
    this.sessionsFiltered$,
    this.sessionFilteredId$
  ]).pipe(
    map(([sessions, id]) => sessions.find(session => session.id === id)),
    filter<Session>(Boolean)
  );

  /** Initialize filtered session id observable */
  private readonly _initSessionFilteredId = (): Observable<string> => combineLatest([
    this._sessionFilteredId,
    this.sessionsFiltered$
  ]).pipe(
    tap(([id, sessions]) => {
      if (!id ||
        (
          id && sessions.every(session => session.id !== id)
        )) {
        const current = sessions.find(session => session.current);

        if (current) {
          this.updateSessionFilteredId(current.id);
          return;
        }

        // No current session found. We are probably between sessions. Pick the next one instead.
        const next = sessions.find(session => isBefore(new Date(), session.start));

        if (next) {
          this.updateSessionFilteredId(next.id);
          return;
        }

        if (sessions.length) {
          // No next session found. Pick the first one in the array.
          const [defaultSession] = sessions;
          this.updateSessionFilteredId(defaultSession.id);
        }
      }
    }),
    map(([id]) => id),
    filter<string>(Boolean),
    distinctUntilChanged()
  );

  /** Initialize custom range ID BehaviorSubject */
  private readonly _initSessionWithCustomRangeId = (): BehaviorSubject<string> => new BehaviorSubject(
    localStorage.getItem(STORAGE_KEY.sessionWithCustomRangeId) || CUSTOM_SESSION_ID
  );

  /** Initialize sessions observable */
  private readonly _initSessions = (): Observable<Session[]> => this._sessions.asObservable()
    .pipe(
      tap(sessions => {
        if (!sessions.length) {
          this._service.list().subscribe(items => this.updateSessions(items));
        }
      }),
      filter(sessions => Boolean(sessions && sessions.length)),
      shareReplay(1)
    );

  /** Initialize filtered sessions observable */
  private readonly _initSessionsFiltered = (): Observable<Session[]> => this.sessions$
    .pipe(
      map(sessions => {
        const third        = '3';
        const schoolYear   = '2020-2021';
        const thirdSession = sessions.find(session => session.name === third && session.schoolYear === schoolYear);

        return sessions.filter(session => {
          const oneWeekFromNow = addWeeks(new Date(), 1);

          return (
            (
              isSameDay(oneWeekFromNow, session.start) || isAfter(oneWeekFromNow, session.start)
            ) &&
            (
              isSameDay(session.start, thirdSession.start) || isAfter(session.start, thirdSession.start)
            )
          );
        });
      })
    );

  /** Initialize custom sessions observable */
  private readonly _initSessionsWithCustomRange = (): Observable<Session[]> => combineLatest([
    this.custom$,
    this.sessions$,
    this._slider.options$
  ])
    .pipe(
      /**
       * Filter out sessions that are outside the slider's maximum range and add the
       * custom range value at the beginning of the array.
       */
      map(([custom, sessions, options]) => {
        const filtered = sessions
          .filter(session => {
            const startSession = session.start.getTime();
            const startSlider  = options.stepsArray[0].value;
            const endSlider    = options.stepsArray[options.stepsArray.length - 1].value;
            return startSession >= startSlider && startSession <= endSlider;
          });

        return [custom, ...filtered];
      }),
      filter(sessions => Boolean(sessions && sessions.length)),
      distinctUntilChanged()
    );

  /** Gets custom range session */
  private readonly _rangeToCustom = (range: Range): Session => {
    const session        = new Session();
    session.endISODate   = format(new Date(range.end), DATE_FNS_ISO_DATE);
    session.id           = CUSTOM_SESSION_ID;
    session.label        = CUSTOM_SESSION_NAME;
    session.name         = CUSTOM_SESSION_NAME;
    session.startISODate = format(new Date(range.start), DATE_FNS_ISO_DATE);
    return session;
  };

  /**
   * This updates the current week state based on new weeks
   * due to change in session
   * @param {Week[]} weeks
   * @private
   */
  private _setWeekOnWeeksUpdate(weeks: Week[]) {
    const isCurrentSelectionPresent = weeks.find(week => week.name === this._weekName$.value);
    const current                   = weeks.find(week => week.active);
    const [first]                   = weeks;

    const selectedWeek = current || isCurrentSelectionPresent || first;
    this.updateWeekName(selectedWeek.name);
  }
}
